import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trophy, Trash2, ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { RoundsTable } from '@/components/scoring/RoundsTable';
import { useHistoryStore } from '@/stores/historyStore';
import { useToast } from '@/hooks/use-toast';
import { formatDateTime } from '@/lib/utils';
import { formatPoints } from '@/lib/scoring';
import { GAME_MODE_CONFIG, MATCH_FORMAT_CONFIG } from '@/types';
import type { MatchWithGames, GameWithTotals } from '@/types';
import { roundRepo } from '@/db/repositories/roundRepo';
import { calculateTotals } from '@/lib/scoring';

export function MatchPage() {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getMatchWithGames, deleteMatch, error, clearError } =
    useHistoryStore();

  const [match, setMatch] = useState<MatchWithGames | null>(null);
  const [gameDetails, setGameDetails] = useState<Record<string, GameWithTotals>>({});
  const [expandedGames, setExpandedGames] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    async function loadMatch() {
      if (matchId) {
        const matchData = await getMatchWithGames(matchId);
        setMatch(matchData);
        setIsLoading(false);
      }
    }
    loadMatch();
  }, [matchId, getMatchWithGames]);

  useEffect(() => {
    if (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error,
      });
      clearError();
    }
  }, [error, toast, clearError]);

  const loadGameDetails = async (gameId: string) => {
    if (gameDetails[gameId]) return; // Already loaded

    const game = match?.games.find(g => g.id === gameId);
    if (!game) return;

    try {
      const rounds = await roundRepo.getByGameId(gameId);
      const totals = calculateTotals(game.participants, rounds);
      setGameDetails(prev => ({
        ...prev,
        [gameId]: { ...game, rounds, totals }
      }));
    } catch (err) {
      console.error('Failed to load game details:', err);
    }
  };

  const toggleGameExpanded = async (gameId: string) => {
    const newExpanded = new Set(expandedGames);
    if (newExpanded.has(gameId)) {
      newExpanded.delete(gameId);
    } else {
      newExpanded.add(gameId);
      await loadGameDetails(gameId);
    }
    setExpandedGames(newExpanded);
  };

  const handleDelete = async () => {
    if (matchId) {
      const success = await deleteMatch(matchId);
      if (success) {
        toast({
          title: 'Match deleted',
          description: 'The match has been removed from history.',
        });
        navigate('/matches');
      }
    }
    setIsDeleteDialogOpen(false);
  };

  if (isLoading) {
    return (
      <div className="container py-6">
        <div className="flex items-center justify-center h-40">
          <p className="text-muted-foreground">Loading match details...</p>
        </div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="container py-6">
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">Match not found.</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate('/matches')}
            >
              Back to Matches
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const winner = match.participants.find((p) => p.id === match.winnerId);
  const formatConfig = MATCH_FORMAT_CONFIG[match.format];

  return (
    <div className="container py-6 space-y-6">
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/matches')}
        className="-ml-2"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Matches
      </Button>

      {/* Match Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{formatConfig.label}</span>
            {winner && (
              <div className="flex items-center gap-1 text-yellow-500">
                <Trophy className="h-5 w-5" />
                <span className="text-base">{winner.name}</span>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Game Mode</span>
            <span>{GAME_MODE_CONFIG[match.mode].label}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Target Score (per game)</span>
            <span>{match.targetScore}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Games Played</span>
            <span>{match.games.filter((g) => g.status === 'completed').length}</span>
          </div>
          {match.completedAt && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Completed</span>
              <span>{formatDateTime(new Date(match.completedAt))}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Final Match Score */}
      <Card>
        <CardHeader>
          <CardTitle>Final Score</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {match.participants.map((participant) => {
              const wins = match.gameWins[participant.id] || 0;
              const isWinner = participant.id === match.winnerId;
              return (
                <div
                  key={participant.id}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    isWinner ? 'bg-yellow-100 dark:bg-yellow-900/20' : 'bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {isWinner && <Trophy className="h-5 w-5 text-yellow-500" />}
                    <span className={isWinner ? 'font-semibold' : ''}>
                      {participant.name}
                    </span>
                  </div>
                  <span className="text-2xl font-bold">{wins}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Games List */}
      <Card>
        <CardHeader>
          <CardTitle>Games ({match.games.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {match.games.map((game) => {
            const gameWinner = match.participants.find(
              (p) => p.id === game.winnerId
            );
            const isExpanded = expandedGames.has(game.id);
            const details = gameDetails[game.id];

            return (
              <Collapsible
                key={game.id}
                open={isExpanded}
                onOpenChange={() => toggleGameExpanded(game.id)}
              >
                <CollapsibleTrigger asChild>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="font-medium">Game {game.gameNumber}</span>
                      {gameWinner && (
                        <span className="text-sm text-muted-foreground">
                          - {gameWinner.name} won
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {game.status === 'completed' ? (
                        <span className="text-xs text-green-600 bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded">
                          Completed
                        </span>
                      ) : (
                        <span className="text-xs text-orange-600 bg-orange-100 dark:bg-orange-900/30 px-2 py-0.5 rounded">
                          In Progress
                        </span>
                      )}
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </div>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  {details ? (
                    <div className="mt-2 p-3 border rounded-lg space-y-4">
                      {/* Game scores */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium">Final Scores</h4>
                        {details.totals
                          .sort((a, b) => b.totalPoints - a.totalPoints)
                          .map((total, index) => (
                            <div
                              key={total.participantId}
                              className="flex items-center justify-between text-sm"
                            >
                              <div className="flex items-center gap-2">
                                <span
                                  className={`font-bold ${
                                    index === 0
                                      ? 'text-yellow-500'
                                      : 'text-muted-foreground'
                                  }`}
                                >
                                  #{index + 1}
                                </span>
                                <span>{total.participantName}</span>
                              </div>
                              <span className="font-medium">
                                {formatPoints(total.totalPoints)}
                              </span>
                            </div>
                          ))}
                      </div>

                      {/* Round history */}
                      {details.rounds.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium mb-2">
                            Rounds ({details.rounds.length})
                          </h4>
                          <RoundsTable
                            rounds={details.rounds}
                            participants={game.participants}
                            targetScore={game.targetScore}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mt-2 p-3 text-center text-sm text-muted-foreground">
                      Loading game details...
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </CardContent>
      </Card>

      {/* Delete Button */}
      <Button
        variant="outline"
        className="w-full text-destructive hover:text-destructive"
        onClick={() => setIsDeleteDialogOpen(true)}
      >
        <Trash2 className="mr-2 h-4 w-4" />
        Delete Match
      </Button>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Match?</DialogTitle>
            <DialogDescription>
              This will permanently delete this match and all its games from
              your history. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
